import { Plate, PlateContent, usePlateEditor } from "platejs/react";
import { styled } from "styled-components";
import { BaseEditorKit } from "@/components/editor/editor-base-kit";
import { FixedToolbarButtons } from "@/components/ui/fixed-toolbar-buttons";
import { FixedToolbar } from "@/components/ui/fixed-toolbar";
import { MarkdownPlugin } from "@platejs/markdown";
import { useEffect, useRef } from "react";

const Editor = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;

  .slate-editor {
    padding: 16px;
    min-height: 200px;
    max-height: 500px;
    overflow: auto;
    // disable focus outline
    &:focus {
      outline: none;
    }
  }
`;

const INITIAL_VALUE = [
  { type: "p", children: [{ text: "" }] },
];

/**
 * RichTextEditor component for editing FAQ answers.
 *
 * Provides a rich text editing experience using Plate.js while storing content
 * as Markdown format. This allows for a WYSIWYG editing experience while maintaining
 * human-readable, portable markdown files.
 *
 * Features:
 * - Rich text editing with formatting toolbar
 * - Saves content as Markdown
 * - Backward compatible with legacy JSON format (automatically converts on load)
 * - Automatically handles empty values
 *
 * @param {Object} props - Component props
 * @param {string} props.initValue - Initial markdown content to display in the editor.
 *                                   Can also accept legacy JSON format (Plate.js node structure)
 *                                   which will be automatically converted to markdown.
 * @param {(markdown: string) => void} props.onChange - Callback function called when content changes.
 *                                                       Receives the serialized markdown string.
 *
 * @example
 * ```tsx
 * <RichTextEditor
 *   initValue="# FAQ Answer\n\nThis is the answer content."
 *   onChange={(markdown) => console.log(markdown)}
 * />
 * ```
 */
export const RichTextEditor = ({ initValue, onChange }: { initValue: string, onChange: (v: string) => void }) => {
  const editor = usePlateEditor({
    plugins: BaseEditorKit,
    value: INITIAL_VALUE,
  });
  const prevInitValueRef = useRef<string>("");

  // Deserialize markdown when initValue changes
  useEffect(() => {
    // Only update if initValue actually changed
    if (initValue === prevInitValueRef.current) {
      return;
    }
    prevInitValueRef.current = initValue;

    if (initValue && initValue.trim()) {
      try {
        // Check if it's old JSON format (starts with [)
        if (initValue.trim().startsWith("[")) {
          // Old format - parse as JSON
          const parsed = JSON.parse(initValue);
          editor.tf.setValue(parsed);
        } else {
          // New markdown format - deserialize
          const nodes = editor.getApi(MarkdownPlugin).markdown.deserialize(initValue);
          editor.tf.setValue(nodes);
        }
      } catch (e) {
        // If parsing fails, try to deserialize as markdown
        try {
          const nodes = editor.getApi(MarkdownPlugin).markdown.deserialize(initValue);
          editor.tf.setValue(nodes);
        } catch (e2) {
          // If all else fails, use empty value
          editor.tf.setValue(INITIAL_VALUE);
        }
      }
    } else if (!initValue || !initValue.trim()) {
      // Empty value - reset to initial
      editor.tf.setValue(INITIAL_VALUE);
    }
  }, [initValue]);

  return (
    <Editor>
      <Plate editor={editor} onChange={() => {
        const md = editor.getApi(MarkdownPlugin).markdown.serialize();
        onChange(md);
      }}>
        <FixedToolbar>
          <FixedToolbarButtons />
        </FixedToolbar>
        <PlateContent placeholder="Write the FAQ answer..." />
      </Plate>
    </Editor>
  );
};
